import React from "react";
import { styled, Theme, Box, Badge, BadgeProps } from "@mui/material";
import { pxToRem } from "../../utils/fontSizeUtility";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleInfo,
  faCircleExclamation,
  faTriangleExclamation,
} from "@fortawesome/sharp-solid-svg-icons";

export type AlertIconType = "critical" | "warning" | "informational";

export interface AlertIconProps {
  type?: AlertIconType;
  count?: number;
  onClick?: () => void;
  className?: string;
}

const alertThemeConfig = (theme: Theme, type: AlertIconType) => {
  switch (type) {
    case "critical":
      return {
        backgroundColor: "#DD0000",
        color: "#FFFFFF",
      };
    case "warning":
      return {
        backgroundColor: "#FDD422", // From Figma: Warning alert color
        color: "#000000",
      };
    case "informational":
      return {
        backgroundColor: "#1E81A1",
        color: "#FFFFFF",
      };
    default:
      const _exhaustiveCheck: never = type;
      return _exhaustiveCheck;
  }
};

const AlertIconContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "type",
})<{ type: AlertIconType }>(({ theme, type }) => {
  const config = alertThemeConfig(theme, type);

  return {
    width: pxToRem(48),
    height: pxToRem(46),
    borderRadius: pxToRem(8),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: config.backgroundColor,
    color: config.color,
    cursor: "pointer",
    position: "relative",
    fontSize: pxToRem(20),
    padding: `${pxToRem(4)} ${pxToRem(12)} ${pxToRem(4)} ${pxToRem(12)}`,
  };
});

const NotificationBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    right: pxToRem(1.6), // 0.1rem
    top: pxToRem(0.48), // 0.03rem
    minWidth: pxToRem(20),
    height: pxToRem(20),
    padding: `0 ${pxToRem(4)}`,
    borderRadius: "50%",
    fontSize: pxToRem(12),
    fontWeight: "bold",
    border: `${pxToRem(2)} solid ${theme.palette.background.paper}`,
  },
}));

export const AlertStatusIcon: React.FC<AlertIconProps> = ({
  type = "informational",
  count = 0,
  onClick,
  className,
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const renderIcon = () => {
    switch (type) {
      case "critical":
        return <FontAwesomeIcon icon={faCircleExclamation} size="lg" data-testid="critical-icon" />;
      case "warning":
        return (
          <FontAwesomeIcon icon={faTriangleExclamation} size="lg" data-testid="warning-icon" />
        );
      case "informational":
        return <FontAwesomeIcon icon={faCircleInfo} size="lg" data-testid="info-icon" />;
      default:
        const _exhaustiveCheck: never = type;
        return _exhaustiveCheck;
    }
  };

  return (
    <NotificationBadge
      badgeContent={count}
      invisible={count <= 0}
      className={className}
      max={99}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <AlertIconContainer
        type={type}
        onClick={handleClick}
        role={onClick ? "button" : undefined}
        aria-label={`${type} alert${count ? ` with ${count} notifications` : ""}`}
        data-testid={`alert-icon-${type}`}
      >
        {renderIcon()}
      </AlertIconContainer>
    </NotificationBadge>
  );
};

export default AlertStatusIcon;
